import React, { useState, useEffect } from 'react';
import { Button, Spin, message } from 'antd';
import styled from 'styled-components';
import { Patient } from './types';
import PatientItem from './components/PatientItem';
import Info from './Info';
import { getPatientList } from '../../services/consultationService';

const Container = styled.div<{ isFullscreen?: boolean }>`
    background: #fff;
    border-radius: ${props => props.isFullscreen ? '0' : '8px'};
    padding: ${props => props.isFullscreen ? '0' : '5px 0'};
    max-width: ${props => props.isFullscreen ? '100%' : '600px'};
    display: flex;
    flex-direction: column;
    ${props => props.isFullscreen && `
        height: 100vh;
        width: 100%;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
    `}
`;

const Header = styled.div<{ isFullscreen?: boolean }>`
    display: flex;
    align-items: center;
    margin-bottom: ${props => props.isFullscreen ? '0' : '16px'};
    padding: ${props => props.isFullscreen ? '17px 24px' : '0'};
    flex-shrink: 0;
    ${props => props.isFullscreen && `
        background: #fff;
        border-bottom: 1px solid #f0f0f0;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 100;
    `}
    
    .title {
        font-size: 14px;
        color: #1f1c2b;
        flex: 1;
    }
`;

const ListContainer = styled.div<{ isFullscreen?: boolean }>`
    overflow-y: auto;
    overflow-x: hidden;
    padding: ${props => props.isFullscreen ? '0 24px' : '0'};
    ${props => props.isFullscreen ? `
        margin-top: 65px;
        margin-bottom: 76px;
        height: calc(100vh - 132px);
    ` : `
        max-height: 60vh;
    `}
    
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #ccc;
        border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
        background: #aaa;
    }
`;

const ButtonGroup = styled.div<{ isFullscreen?: boolean }>`
    display: flex;
    gap: 8px;
    padding: ${props => props.isFullscreen ? '12px 24px' : '0'};
    flex-shrink: 0;
    ${props => props.isFullscreen && `
        background: #fff;
        border-top: 1px solid #f0f0f0;
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 100;
    `}

    button {
        flex: 1;
        height: 40px;
        font-size: 14px;
    }
`;

interface PatientListProps {
    onSelect?: (patient: Patient | null) => void;
    accountId: string;
    templateId: string;
    doctorId: string;
    isFullscreen?: boolean;
    onNavigate?: () => void;
}

const PatientList: React.FC<PatientListProps> = ({
    accountId,
    templateId,
    doctorId,
    onSelect,
    isFullscreen,
    onNavigate
}) => {
    const [showAddForm, setShowAddForm] = useState(false);
    const [patients, setPatients] = useState<Patient[]>([]);
    const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchPatients();
    }, [accountId]);

    const fetchPatients = async () => {
        try {
            const data = await getPatientList(accountId);
            setPatients(data);
        } catch (error) {
            console.error('获取就诊人列表失败:', error);
            message.error('获取就诊人列表失败');
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = (patient: Patient) => {
        const newSelected = selectedPatient?.patientId === patient.patientId ? null : patient;
        setSelectedPatient(newSelected);
        onSelect?.(newSelected);
    };

    const handleAddPatient = () => {
        setShowAddForm(true);
        setSelectedPatient(null);
        onNavigate?.();
    };

    const handleSubmitPatient = async (values: any) => {
        try {
            setShowAddForm(false);
            await fetchPatients();
        } catch (error) {
            message.error('添加就诊人失败');
        }
    };

    if (showAddForm) {
        return (
            <Info 
                accountId={accountId}
                templateId={templateId}
                doctorId={doctorId}
                onSubmit={handleSubmitPatient}
                onBack={() => setShowAddForm(false)}
                isFullscreen={isFullscreen}
            />
        );
    }

    if (loading) {
        return (
            <Container isFullscreen={isFullscreen}>
                <Spin />
            </Container>
        );
    }

    return (
        <Container isFullscreen={isFullscreen}>
            <Header isFullscreen={isFullscreen}>
                <div className="title">选择就诊人</div>
            </Header>

            <ListContainer isFullscreen={isFullscreen}>
                {patients.map(patient => (
                    <PatientItem
                        key={patient.patientId}
                        patient={patient}
                        selected={selectedPatient?.patientId === patient.patientId}
                        onClick={() => handleSelect(patient)}
                    />
                ))}
            </ListContainer>

            <ButtonGroup isFullscreen={isFullscreen}>
                <Button onClick={handleAddPatient} block>
                    新建就诊人
                </Button>
                <Button 
                    type="primary"
                    disabled={!selectedPatient}
                    onClick={() => selectedPatient && onSelect?.(selectedPatient)}
                    block
                >
                    {selectedPatient ? '开始问诊' : '请选择就诊人'}
                </Button>
            </ButtonGroup>
        </Container>
    );
};

export default PatientList;